import { Check, ChevronDown, Flag, PenLine, Scissors, Sparkles } from "lucide-react";
import { Fragment, memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { type ReviewSegment, type ReviewWord, type TranscriptSpeaker } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatClockAt, formatTimecode } from "@/lib/format";
import { SplitMeetingForm } from "@/pages/recording/SplitMeetingForm";
import { speakerColour, speakerName } from "@/lib/speakerColour";
import { Editor, Teach, Proposals } from "@/pages/review/SegmentEditing";

/**
 * Below this, a word gets the dotted amber underline that says "listen closely".
 *
 * The handoff's own seed dialogue names the number — *"anything under zero point eight
 * stays unverified"*. It is a display rule and lives here rather than on the Server,
 * where it would be a threshold baked into stored data.
 *
 * **Null is not low.** A word the aligner could not match back carries no confidence at
 * all, and so does a word a person typed; both are left unmarked. Drawing them as
 * suspect would send the owner listening for hundreds of words that are probably fine —
 * and would mark the very words they just corrected.
 */
const LOW_CONFIDENCE = 0.8;

interface SegmentProps {
  segment: ReviewSegment;
  index: number;
  speakers: TranscriptSpeaker[];
  startedAt: string | null;
  editing: boolean;
  /** Which word the editor should open on and select, and `null` to open on none. */
  editingWord: number | null;
  /** The word just corrected inside this segment, offered to the vocabulary; `null` for
   * every segment that is not the one somebody just typed in. */
  teach: string | null;
  menuOpen: boolean;
  saving: boolean;
  /** Whether "Split here" may appear on this turn at all — false for the very first turn
   * even when the screen as a whole is splittable, the same rule the Transcript tab's own
   * "Split here" uses. */
  splittable: boolean;
  /** Parts mode (SPCH-14): the scissors start a new part in the draft rather than opening
   * SPCH-12's split form. */
  cutsParts: boolean;
  /** Inside a part set to discard, drawn receded — still readable, still correctable. */
  discarded: boolean;
  splitOpen: boolean;
  onSeek: (seconds: number) => void;
  onFlag: (segmentId: string) => void;
  onEdit: (segmentId: string, word?: number | null, startMs?: number) => void;
  onCloseEditor: (segmentId?: string) => void;
  onTeach: (segmentId: string, word: string) => void;
  onCloseTeach: () => void;
  onMenu: (segmentId: string) => void;
  onReassign: (segmentId: string, speakerId: string | null) => void;
  onCorrect: (segmentId: string, text: string) => void;
  onRevert: (segmentId: string) => Promise<unknown>;
  onResolveProposal: (correctionId: string, apply: boolean) => Promise<unknown>;
  onSplit: (segmentId: string) => void;
  onSubmitSplit: (input: { title: string; description?: string; fromSequence: number }) => Promise<void>;
}

/**
 * Memoized, and that is the point of the component existing at all.
 *
 * Six hundred of these are on the page. Flagging one, opening one menu or typing in one
 * editor must cost one re-render, not six hundred — so every prop here is either the
 * segment's own row or a callback whose identity never changes.
 */
export const Segment = memo(function Segment({
  segment,
  index,
  speakers,
  startedAt,
  editing,
  editingWord,
  teach,
  menuOpen,
  saving,
  splittable,
  cutsParts,
  discarded,
  splitOpen,
  onSeek,
  onFlag,
  onEdit,
  onCloseEditor,
  onTeach,
  onCloseTeach,
  onMenu,
  onReassign,
  onCorrect,
  onRevert,
  onResolveProposal,
  onSplit,
  onSubmitSplit,
}: SegmentProps) {
  const speakerIndex = speakers.findIndex((speaker) => speaker.id === segment.speakerId);
  const speaker = speakerIndex >= 0 ? speakers[speakerIndex] : undefined;
  const colour = speaker ? speakerColour(speakerIndex) : "var(--text-muted)";
  const flagged = segment.flaggedAt !== null;

  // Six hundred segments carry six hundred flag buttons, and "Flag this segment" names
  // every one of them identically — which is a list of indistinguishable controls to
  // anybody reading the page through a screen reader. The timecode is the one thing
  // that tells them apart, and it is already on screen next to each.
  const at = formatClockAt(startedAt, segment.startsAtMs) ?? formatTimecode(segment.startsAtMs);

  // The whitespace between the words, taken from the segment's own text rather than
  // invented as one space per gap. It is what makes the sentence occupy the same width
  // read as it does typed: the editor holds that exact text, so anything this screen
  // put between the words instead would break the lines somewhere else.
  const gaps = useMemo(() => wordGaps(segment.text, segment.words), [segment.text, segment.words]);

  // The reassign menu is portalled to the body and positioned from this rect. It has to
  // be: each segment carries `content-visibility: auto` for scroll performance, which
  // makes it its own containing block, so an absolutely-positioned child can never
  // escape it — the menu would paint underneath whichever segment comes next in the
  // DOM no matter how high its z-index goes.
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  /**
   * A finished correction: what to send, and what it turns out to have been.
   *
   * The Server stores a segment's whole text, so that is what goes — but the two things
   * worth knowing about an edit are found by comparing it with what was there before.
   * Nothing changed means nothing is sent at all, which is what lets clicking away from
   * a sentence somebody only wanted to read cost nothing. And exactly one word swapped
   * for exactly one word is the shape of "the transcriber misheard a name", which is the
   * only shape worth offering to the vocabulary: a deletion has nothing to learn, and a
   * rewritten sentence is somebody fixing grammar rather than naming a thing.
   */
  const save = (typed: string) => {
    const text = typed.trim();

    if (text === segment.text) return;

    onCorrect(segment.id, text);

    const taught = swappedWord(segment.text, text);
    if (taught !== null) onTeach(segment.id, taught);
  };

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPos(null);
      return;
    }

    const place = () => {
      const rect = menuTrigger.current?.getBoundingClientRect();
      if (rect) setMenuPos({ top: rect.bottom + 4, left: rect.left });
    };

    place();
    // `capture: true` catches scrolling on the reading column itself, not just the window.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [menuOpen]);

  return (
    <div
      data-segment={segment.id}
      className={cn(
        "relative flex gap-[var(--space-4)] rounded-[var(--radius-xl)] border p-[var(--space-4)] transition-colors",
        "[&:has([data-current])]:border-[color-mix(in_oklch,var(--vl-bridge-soft)_28%,transparent)]",
        "[&:has([data-current])]:bg-[color-mix(in_oklch,var(--surface-2)_32%,transparent)]",
        flagged
          ? "border-[color-mix(in_oklch,var(--warning)_30%,transparent)] bg-[color-mix(in_oklch,var(--warning)_5%,transparent)]"
          : "border-transparent",
        discarded && "opacity-55 [&:has([data-current])]:opacity-100",
      )}
      // Six hundred segments of which a dozen are on screen. The browser skips layout
      // and paint for the rest, and the intrinsic size keeps the scrollbar honest while
      // it does — which is what makes a three-hour meeting scroll like a short one.
      style={{ contentVisibility: "auto", containIntrinsicSize: "auto 120px" }}
    >
      <button
        type="button"
        onClick={() => segment.startsAtMs !== null && onSeek(segment.startsAtMs / 1000)}
        className="w-[46px] shrink-0 pt-[3px] text-left font-mono text-[length:var(--text-2xs)] text-[var(--text-muted)] transition-colors hover:text-[var(--vl-bridge-soft)]"
        aria-label={`Play from ${at}`}
      >
        {at}
      </button>

      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-2">
          <button
            ref={menuTrigger}
            type="button"
            onClick={() => onMenu(segment.id)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label={`Change the speaker of the segment at ${at}`}
            className="inline-flex items-center gap-[7px] rounded-[var(--radius-full)] border border-transparent py-0.5 pl-1 pr-2 text-[length:var(--text-xs)] font-semibold transition-colors hover:border-[var(--border-default)] hover:bg-[var(--surface-glass)]"
            style={{ color: colour }}
          >
            <span aria-hidden className="h-[7px] w-[7px] rounded-full" style={{ background: colour }} />
            {speaker ? speakerName(speaker, speakerIndex) : "Unattributed"}
            <ChevronDown size={11} strokeWidth={2} />
          </button>

          {flagged && (
            <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--warning)] px-2 py-px text-[length:var(--text-2xs)] text-[var(--warning)]">
              <Flag size={10} strokeWidth={2} /> Flagged
            </span>
          )}

          {segment.correction && (
            <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--vl-life-soft)] px-2 py-px text-[length:var(--text-2xs)] text-[var(--vl-life-soft)]">
              <PenLine size={10} strokeWidth={2} /> Corrected
            </span>
          )}

          {segment.proposals.some((proposal) => proposal.status === "pending") && (
            <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--warning)] px-2 py-px text-[length:var(--text-2xs)] text-[var(--warning)]">
              <Sparkles size={10} strokeWidth={2} /> Unverified
            </span>
          )}

          <div className="ml-auto flex items-center gap-0.5 opacity-35 transition-opacity focus-within:opacity-100 group-hover:opacity-100 hover:opacity-100">
            <button
              type="button"
              onClick={() => onFlag(segment.id)}
              title="Flag as wrong (F)"
              aria-pressed={flagged}
              aria-label={flagged ? `Remove the flag on the segment at ${at}` : `Flag the segment at ${at} as wrong`}
              className={cn(
                "inline-flex h-[26px] w-[26px] items-center justify-center rounded-[var(--radius-md)] transition-colors hover:bg-[var(--surface-glass)] hover:text-[var(--warning)]",
                flagged ? "text-[var(--warning)]" : "text-[var(--text-muted)]",
              )}
            >
              <Flag size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => onEdit(segment.id)}
              title="Edit text (E)"
              aria-label={`Correct the segment at ${at}`}
              className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-glass)] hover:text-[var(--text-primary)]"
            >
              <PenLine size={14} strokeWidth={2} />
            </button>
            {splittable && (
              <button
                type="button"
                onClick={() => onSplit(segment.id)}
                title={cutsParts ? "Start a new part here (S)" : "Split into a new meeting from here"}
                aria-pressed={cutsParts ? undefined : splitOpen}
                aria-label={cutsParts ? `Start a new part at ${at}` : `Split into a new meeting starting at ${at}`}
                className={cn(
                  "inline-flex h-[26px] w-[26px] items-center justify-center rounded-[var(--radius-md)] transition-colors hover:bg-[var(--surface-glass)] hover:text-[var(--text-primary)]",
                  splitOpen ? "text-[var(--primary)]" : "text-[var(--text-muted)]",
                )}
              >
                <Scissors size={14} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>

        {menuOpen && menuPos && createPortal(
          <div
            role="menu"
            aria-label={`Reassign the segment at ${at}`}
            className="fixed z-[60] flex w-[250px] flex-col gap-0.5 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-3)] p-1.5 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)]"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <p className="px-2 pb-1 pt-1.5 text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Reassign this segment
            </p>

            {speakers.map((option, position) => (
              <button
                key={option.id}
                type="button"
                role="menuitem"
                onClick={() => onReassign(segment.id, option.id)}
                className="flex items-center gap-[9px] rounded-[var(--radius-md)] px-2 py-1.5 text-left text-[length:var(--text-sm)] text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-glass-md)]"
              >
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: speakerColour(position) }}
                />
                <span className="flex-1">{speakerName(option, position)}</span>
                {option.id === segment.speakerId && (
                  <Check size={13} strokeWidth={2.5} className="text-[var(--vl-life-soft)]" />
                )}
              </button>
            ))}

            <button
              type="button"
              role="menuitem"
              onClick={() => onReassign(segment.id, null)}
              className="flex items-center gap-[9px] rounded-[var(--radius-md)] px-2 py-1.5 text-left text-[length:var(--text-sm)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-glass-md)]"
            >
              <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-[var(--text-muted)]" />
              <span className="flex-1">Nobody</span>
              {segment.speakerId === null && <Check size={13} strokeWidth={2.5} className="text-[var(--vl-life-soft)]" />}
            </button>
          </div>,
          document.body,
        )}

        {editing ? (
          <Editor
            segment={segment}
            saving={saving}
            // Which characters of the sentence to have selected when it opens. The word
            // spans are the segment's own text, walked, so this is the range the clicked
            // word occupies in the field about to hold it.
            select={editingWord === null ? null : (wordSpans(segment.text, segment.words)?.[editingWord] ?? null)}
            onClose={() => onCloseEditor(segment.id)}
            onSave={save}
            onRevert={() => onRevert(segment.id)}
          />
        ) : (
          <p className="text-[19px] leading-[1.85] tracking-[-0.005em] [text-wrap:pretty]">
            {segment.words.length === 0
              ? segment.text
              : segment.words.map((word, position) => {
                  const low = word.confidence !== null && word.confidence < LOW_CONFIDENCE;

                  return (
                    <Fragment key={`${segment.id}:${position}`}>
                      {gaps[position]}
                      <span
                        data-w={`${segment.id}:${position}`}
                        data-seg={index}
                        onClick={() => onEdit(segment.id, position, word.startMs)}
                        title={low ? "Low confidence — listen closely" : undefined}
                        className={cn(
                          // Laid out as plain text — no inline-block, and the highlight's
                          // padding handed straight back as negative margin — so a word
                          // takes up exactly the width its letters do and the gaps between
                          // them are real spaces. A margin or a padding that keeps its
                          // width here is a few pixels per word the editor does not have,
                          // which is a line break in a different place and a sentence that
                          // jumps the moment it opens.
                          "-mx-0.5 cursor-text rounded-[5px] px-0.5 transition-[background-color,color] duration-[130ms]",
                          // The whole invitation, and it needs to be no louder than this:
                          // a word the pointer is on is a word that will open when clicked.
                          "hover:bg-[color-mix(in_oklch,var(--vl-bridge)_22%,transparent)] hover:text-[var(--text-primary)]",
                          // Unspoken words are dimmed and spoken ones are not, and the
                          // switch is made by the same attribute the highlight uses — so
                          // one DOM write moves both.
                          "text-[var(--text-muted)] [&[data-current]]:text-[var(--text-primary)]",
                          "[&[data-current]]:bg-[linear-gradient(120deg,color-mix(in_oklch,var(--vl-bridge)_55%,transparent),color-mix(in_oklch,var(--vl-accent-warm)_50%,transparent))]",
                          // Underlined rather than bottom-bordered, because a border is a
                          // pixel of box: it makes every line of the sentence a pixel
                          // taller than the same line inside the editor, and the words
                          // below shuffle as one opens and closes. An underline is paint.
                          low
                            ? "underline decoration-dotted decoration-[color-mix(in_oklch,var(--warning)_55%,transparent)] underline-offset-[5px]"
                            : "no-underline",
                        )}
                      >
                        {word.word}
                      </span>
                    </Fragment>
                  );
                })}
            {segment.words.length > 0 && gaps[segment.words.length]}
          </p>
        )}

        {teach !== null && !editing && <Teach word={teach} onClose={onCloseTeach} />}

        {segment.proposals.length > 0 && !editing && (
          <Proposals proposals={segment.proposals} saving={saving} onResolve={onResolveProposal} />
        )}

        {splitOpen && (
          <SplitMeetingForm
            fromSequence={segment.sequence}
            onCancel={() => onSplit(segment.id)}
            onSplit={onSubmitSplit}
          />
        )}
      </div>
    </div>
  );
});

// ------------------------------------------------------------------ reading the edit

/**
 * Where each of a segment's words sits inside the segment's own text, or `null` when
 * they cannot be walked through it in order.
 *
 * They almost always can: the aligner's words are that text's tokens, and a corrected
 * segment's words are the Server's own split of it. But "almost always" is not a thing
 * to place somebody's caret on, so a mismatch says so and the sentence opens with
 * nothing selected rather than with the wrong thing selected.
 */
/**
 * The text that sits before each word of a segment — the leading gap of word 0, then the
 * gap between each pair, and last the tail after the final word.
 *
 * Reading mode draws one span per word, so something has to go between them; taking it
 * from the segment's own text keeps the spacing identical to the editor's, which holds
 * that text verbatim. When the words cannot be walked through the text (see
 * `wordSpans`), a single space between each is the honest fallback.
 */
function wordGaps(text: string, words: ReviewWord[]): string[] {
  const spans = wordSpans(text, words);
  if (!spans) return words.map((_, position) => (position === 0 ? "" : " ")).concat("");

  const gaps: string[] = [];
  let at = 0;

  for (const [start, end] of spans) {
    gaps.push(text.slice(at, start));
    at = end;
  }

  gaps.push(text.slice(at));

  return gaps;
}

function wordSpans(text: string, words: ReviewWord[]): [number, number][] | null {
  const spans: [number, number][] = [];
  let at = 0;

  for (const word of words) {
    const start = text.indexOf(word.word, at);
    if (start < 0) return null;

    spans.push([start, start + word.word.length]);
    at = start + word.word.length;
  }

  return spans;
}

/**
 * The one word that changed, when a correction changed exactly one — and `null` for
 * every other kind of edit.
 *
 * This is what decides whether the vocabulary offer appears. Same number of words with
 * a difference at one of them is somebody replacing a misheard name, which is worth
 * teaching. A word added, a word deleted, two words changed, a sentence rewritten: all
 * of those are somebody fixing a sentence, and offering to teach the system a word out
 * of them would be guessing.
 */
function swappedWord(before: string, after: string): string | null {
  const from = before.trim().split(/\s+/u).filter(Boolean);
  const to = after.trim().split(/\s+/u).filter(Boolean);
  if (from.length !== to.length) return null;

  let changed: string | null = null;

  for (let index = 0; index < from.length; index += 1) {
    if (from[index] === to[index]) continue;
    if (changed !== null) return null;

    changed = to[index]!;
  }

  return changed;
}

// ------------------------------------------------------------------ teaching a word (0035)
